import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import SignaturePad from '@/components/SignaturePad';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  FileText, Download, CheckCircle, AlertCircle, Clock, Plus, 
  User, Users, Phone, MapPin, Heart, Syringe, Shield, Car, ClipboardList, Upload, Baby, Send, Mail, Loader2, Link2, Copy, Handshake, PenTool
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import DCFEnrollmentForm from './DCFEnrollmentForm';
import DCFHealthReportForm from './DCFHealthReportForm';
import DCFIntakeUnder2Form from './DCFIntakeUnder2Form';
import DCFHealthHistoryForm from './DCFHealthHistoryForm';

import API from '@/config/api';
// API imported from config;

const FORM_TYPES = [
  { id: 'enrollment', label: 'Child Care Enrollment', icon: ClipboardList, required: true, dcfCode: 'DCF 251.04(6)(a)1-2' },
  { id: 'health_history', label: 'Intake for Child Under 2 Years', icon: Baby, required: true, dcfCode: 'DCF-F-CFS0061', under2Only: true },
  { id: 'immunization', label: 'Immunization Record', icon: Syringe, required: true, dcfCode: 'DCF 251.04(6)(a)6m' },
  { id: 'physical_exam', label: 'Child Health Report', icon: User, required: true, dcfCode: 'DCF 251.04(6)(a)8' },
  { id: 'emergency_info', label: 'Health History & Emergency Care Plan', icon: Heart, required: false, dcfCode: 'DCF-F-CFS2345-E' },
  { id: 'field_trip', label: 'Field Trip Permission', icon: Car, required: false, dcfCode: 'DCF 251.04(6)(a)4' },
  { id: 'alternate_arrival', label: 'Alternate Arrival/Release Agreement', icon: Shield, required: false, dcfCode: 'DCF 251.04(6)(a)5' },
];

const ChildRecordsForms = ({ childId, childName, childDob, onUpdate }) => {
  const { user, getAuthHeaders, token } = useAuth();

  // Calculate if child is under 2
  const isChildUnder2 = (() => {
    if (!childDob) return false;
    try {
      const dob = new Date(childDob + 'T00:00:00');
      const now = new Date();
      const ageMs = now - dob;
      const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
      return ageYears < 2;
    } catch { return false; }
  })();
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState({});
  const [dcfEnrollmentOpen, setDcfEnrollmentOpen] = useState(false);
  const [dcfHealthReportOpen, setDcfHealthReportOpen] = useState(false);
  const [dcfIntakeUnder2Open, setDcfIntakeUnder2Open] = useState(false);
  const [dcfHealthHistoryOpen, setDcfHealthHistoryOpen] = useState(false);
  
  // Provider/Parent Agreement state
  const [agreement, setAgreement] = useState({ signed: false, uploaded_file: null });
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  
  // Magic link enrollment state
  const [sendLinkDialogOpen, setSendLinkDialogOpen] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [magicLinkData, setMagicLinkData] = useState({
    parent_email: '',
    parent_name: '',
    forms_to_complete: ['enrollment', 'health_history']
  });
  const [sentLink, setSentLink] = useState(null);
  const [existingLinks, setExistingLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  // Sibling picker for multi-child magic link
  const [siblings, setSiblings] = useState([]);
  const [selectedSiblingIds, setSelectedSiblingIds] = useState([]);
  // Countersign state
  const [countersignOpen, setCountersignOpen] = useState(false);
  const [countersignSig, setCountersignSig] = useState('');
  const [countersignTyped, setCountersignTyped] = useState('');
  const [countersignDate, setCountersignDate] = useState(new Date().toISOString().slice(0, 10));
  const [countersignName, setCountersignName] = useState('');
  const [countersigning, setCountersigning] = useState(false);

  useEffect(() => {
    if (childId) {
      fetchForms();
      fetchAgreement();
      fetchSiblings();
    }
  }, [childId]);

  const fetchSiblings = async () => {
    try {
      const r = await axios.get(`${API}/children/${childId}/siblings`, getAuthHeaders());
      setSiblings(r.data?.siblings || []);
    } catch (e) {
      setSiblings([]);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms`, getAuthHeaders());
      setForms(response.data.forms || {});
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/provider-parent-agreement`, getAuthHeaders());
      setAgreement(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement:', error);
    }
  };

  const handleAgreementToggle = async (signed) => {
    setAgreementLoading(true);
    try {
      await axios.post(
        `${API}/children/${childId}/provider-parent-agreement`,
        { signed, signed_by: user?.name || user?.email },
        getAuthHeaders()
      );
      setAgreement(prev => ({ ...prev, signed }));
      toast.success(signed ? 'Agreement marked as signed' : 'Agreement marked as unsigned');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update agreement');
    } finally {
      setAgreementLoading(false);
    }
  };

  const handleAgreementUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingAgreement(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/children/${childId}/provider-parent-agreement/upload`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Agreement uploaded successfully');
      fetchAgreement();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to upload agreement');
    } finally {
      setUploadingAgreement(false);
      event.target.value = '';
    }
  };

  const handleCountersignSubmit = async () => {
    if (!countersignSig) {
      toast.error('Please draw or type a signature first');
      return;
    }
    setCountersigning(true);
    try {
      const res = await axios.post(
        `${API}/children/${childId}/provider-parent-agreement/countersign`,
        {
          provider_signature: countersignSig,
          provider_signature_date: countersignDate,
          provider_contact_name: countersignName || (user?.name || ''),
        },
        getAuthHeaders()
      );
      const mirrored = res.data?.mirrored_to_siblings || 0;
      toast.success(
        mirrored > 0
          ? `Countersigned • also applied to ${mirrored} sibling${mirrored !== 1 ? 's' : ''}`
          : 'Agreement countersigned'
      );
      setCountersignOpen(false);
      setCountersignSig('');
      setCountersignTyped('');
      fetchAgreement();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Countersign failed');
    } finally {
      setCountersigning(false);
    }
  };

  const handleDownloadAgreement = async () => {    try {
      const response = await axios.get(
        `${API}/children/${childId}/provider-parent-agreement/download`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Provider_Parent_Agreement_${childName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Agreement downloaded');
    } catch (error) {
      toast.error('Failed to download agreement');
    }
  };

  const openFormDialog = (formType) => {
    // Use dedicated DCF Enrollment Form for enrollment type
    if (formType.id === 'enrollment') {
      setDcfEnrollmentOpen(true);
      return;
    }
    // Use dedicated DCF Health Report Form for physical_exam type
    if (formType.id === 'physical_exam') {
      setDcfHealthReportOpen(true);
      return;
    }
    // Use dedicated DCF Intake Under 2 Form for health_history type
    if (formType.id === 'health_history') {
      setDcfIntakeUnder2Open(true);
      return;
    }
    // Use dedicated DCF Health History Form for emergency_info type
    if (formType.id === 'emergency_info') {
      setDcfHealthHistoryOpen(true);
      return;
    }
    setActiveForm(formType);
    setFormData(forms[formType.id] || getDefaultFormData(formType.id));
    setFormDialogOpen(true);
  };

  const getDefaultFormData = (formId) => {
    const defaults = {
      enrollment: {
        child_name: childName,
        child_dob: '',
        parent1_name: '',
        parent2_name: '',
        home_address: '',
        home_phone: '',
        parent1_work_phone: '',
        parent2_work_phone: '',
        parent1_cell: '',
        parent2_cell: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        physician_name: '',
        physician_phone: '',
        physician_address: '',
        medical_facility: '',
        authorized_pickups: [{ name: '', phone: '', relationship: '' }],
        first_day_attendance: '',
        emergency_medical_consent: false,
        photo_consent: false,
        sunscreen_consent: false,
        parent_signature: '',
        signature_date: new Date().toISOString().split('T')[0]
      },
      health_history: {
        child_name: childName,
        child_dob: '',
        parent_name: '',
        parent_phone: '',
        physician_name: '',
        physician_phone: '',
        physician_address: '',
        medical_conditions: {
          asthma: false,
          diabetes: false,
          epilepsy: false,
          cerebral_palsy: false,
          food_allergies: false,
          other: false
        },
        condition_details: '',
        allergies: '',
        allergy_triggers: '',
        allergy_symptoms: '',
        allergy_treatment: '',
        when_to_call_parent: '',
        when_emergency_care: '',
        trained_staff: '',
        special_diet: '',
        milk_allergy_alternative: '',
        medications: '',
        parent_signature: '',
        signature_date: new Date().toISOString().split('T')[0]
      },
      immunization: {
        child_name: childName,
        child_dob: '',
        dtap_dates: ['', '', '', '', ''],
        polio_dates: ['', '', '', ''],
        mmr_dates: ['', ''],
        hib_dates: ['', '', '', ''],
        hepb_dates: ['', '', ''],
        varicella_dates: ['', ''],
        religious_exemption: false,
        personal_exemption: false,
        medical_exemption: false,
        exemption_signature: '',
        parent_signature: '',
        signature_date: new Date().toISOString().split('T')[0]
      },
      physical_exam: {
        child_name: childName,
        child_dob: '',
        exam_date: '',
        physician_name: '',
        physician_signature: '',
        height: '',
        weight: '',
        blood_pressure: '',
        vision: '',
        hearing: '',
        general_health: 'good',
        restrictions: '',
        recommendations: '',
        next_exam_due: ''
      },
      field_trip: {
        child_name: childName,
        blanket_permission: false,
        specific_trips: [],
        transportation_consent: false,
        emergency_info_carried: true,
        parent_signature: '',
        signature_date: new Date().toISOString().split('T')[0]
      },
      alternate_arrival: {
        child_name: childName,
        arrives_from: '',
        arrives_via: '',
        arrival_time: '',
        departs_to: '',
        departs_via: '',
        departure_time: '',
        transportation_agency: '',
        special_instructions: '',
        parent_signature: '',
        signature_date: new Date().toISOString().split('T')[0]
      }
    };
    return defaults[formId] || {};
  };

  const handleSaveForm = async () => {
    try {
      await axios.put(
        `${API}/children/${childId}/forms/${activeForm.id}`,
        formData,
        getAuthHeaders()
      );
      toast.success(`${activeForm.label} saved successfully`);
      setFormDialogOpen(false);
      fetchForms();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save form');
    }
  };

  const handleDownloadPDF = async (formType) => {
    try {
      // Use dedicated endpoint for DCF forms
      let endpoint;
      let method = 'post';
      
      if (formType.id === 'enrollment') {
        endpoint = `${API}/children/${childId}/forms/dcf-0062`;
        method = 'get';
      } else if (formType.id === 'physical_exam') {
        endpoint = `${API}/children/${childId}/forms/dcf-0060`;
        method = 'get';
      } else if (formType.id === 'health_history') {
        endpoint = `${API}/children/${childId}/forms/dcf-0061`;
        method = 'get';
      } else if (formType.id === 'emergency_info') {
        endpoint = `${API}/children/${childId}/forms/dcf-2345`;
        method = 'get';
      } else {
        endpoint = `${API}/children/${childId}/forms/${formType.id}/pdf`;
      }
      
      const response = method === 'get'
        ? await axios.get(endpoint, { ...getAuthHeaders(), responseType: 'blob' })
        : await axios.post(endpoint, {}, { ...getAuthHeaders(), responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formType.label.replace(/\s+/g, '_')}_${childName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Form downloaded');
    } catch (error) {
      toast.error('Failed to download form');
    }
  };

  const handleSendMagicLink = async () => {
    if (!magicLinkData.parent_email) {
      toast.error('Please enter parent email');
      return;
    }
    if (magicLinkData.forms_to_complete.length === 0) {
      toast.error('Please select at least one form');
      return;
    }

    setSendingLink(true);
    try {
      const response = await axios.post(
        `${API}/enrollment/send-magic-link`,
        {
          child_id: childId,
          additional_child_ids: selectedSiblingIds,
          parent_email: magicLinkData.parent_email,
          parent_name: magicLinkData.parent_name,
          forms_to_complete: magicLinkData.forms_to_complete
        },
        getAuthHeaders()
      );
      
      setSentLink(response.data);
      toast.success(`Enrollment link sent to ${magicLinkData.parent_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send enrollment link');
    } finally {
      setSendingLink(false);
    }
  };

  const copyLinkToClipboard = () => {
    if (sentLink?.link) {
      navigator.clipboard.writeText(sentLink.link);
      toast.success('Link copied to clipboard');
    }
  };

  const resetSendLinkDialog = () => {
    setMagicLinkData({
      parent_email: '',
      parent_name: '',
      forms_to_complete: ['enrollment', 'health_history']
    });
    setSelectedSiblingIds([]);
    setSentLink(null);
  };

  const fetchExistingLinks = async () => {
    setLoadingLinks(true);
    try {
      const response = await axios.get(
        `${API}/enrollment/magic-links?child_id=${childId}`,
        getAuthHeaders()
      );
      setExistingLinks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch existing links:', error);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleSendReminder = async (linkId) => {
    setSendingReminder(linkId);
    try {
      const r = await axios.post(
        `${API}/enrollment/magic-links/${linkId}/send-reminder`,
        {},
        getAuthHeaders()
      );
      const missing = r.data?.per_child_missing || [];
      if (r.data?.is_multi_child && missing.length) {
        const summary = missing.map(m => `${m.child_name} (${m.missing_forms.length})`).join(', ');
        toast.success(`Reminder sent • pending: ${summary}`);
      } else {
        toast.success('Reminder sent successfully');
      }
      fetchExistingLinks(); // Refresh to show updated reminder count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleFileUpload = async (formType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (PDF, JPG, PNG)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(prev => ({ ...prev, [formType.id]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('form_type', formType.id);

      await axios.post(
        `${API}/children/${childId}/forms/${formType.id}/upload`,
        formDataUpload,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`${formType.label} uploaded successfully`);
      fetchForms(); // Refresh forms
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(prev => ({ ...prev, [formType.id]: false }));
      // Reset the file input
      event.target.value = '';
    }
  };

  const getFormStatus = (formId) => {
    const form = forms[formId];
    if (!form) return { status: 'missing', label: 'Not Started', color: 'bg-gray-100 text-gray-600' };
    if (form.uploaded_file) return { status: 'uploaded', label: 'Uploaded', color: 'bg-blue-100 text-blue-600' };
    if (form.completed) return { status: 'complete', label: 'Complete', color: 'bg-green-100 text-green-600' };
    return { status: 'incomplete', label: 'In Progress', color: 'bg-yellow-100 text-yellow-600' };
  };

  const addAuthorizedPickup = () => {
    setFormData({
      ...formData,
      authorized_pickups: [...(formData.authorized_pickups || []), { name: '', phone: '', relationship: '' }]
    });
  };

  const updateAuthorizedPickup = (index, field, value) => {
    const pickups = [...(formData.authorized_pickups || [])];
    pickups[index] = { ...pickups[index], [field]: value };
    setFormData({ ...formData, authorized_pickups: pickups });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div></div>;
  }

  const renderFormContent = () => {
    if (!activeForm) return null;

    switch (activeForm.id) {
      case 'enrollment':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              {/* Child Information */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Child Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Child's Full Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
                  <div><Label>Date of Birth</Label><Input type="date" value={formData.child_dob || ''} onChange={(e) => setFormData({...formData, child_dob: e.target.value})} /></div>
                </div>
                <div className="mt-3"><Label>Home Address</Label><Input value={formData.home_address || ''} onChange={(e) => setFormData({...formData, home_address: e.target.value})} placeholder="Street, City, State, ZIP" /></div>
                <div className="mt-3"><Label>Home Phone</Label><Input value={formData.home_phone || ''} onChange={(e) => setFormData({...formData, home_phone: e.target.value})} /></div>
                <div className="mt-3"><Label>First Day of Attendance</Label><Input type="date" value={formData.first_day_attendance || ''} onChange={(e) => setFormData({...formData, first_day_attendance: e.target.value})} /></div>
              </div>
              
              <Separator />
              
              {/* Parent 1 */}
              <div>
                <h3 className="font-semibold mb-3">Parent/Guardian 1</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Full Name</Label><Input value={formData.parent1_name || ''} onChange={(e) => setFormData({...formData, parent1_name: e.target.value})} /></div>
                  <div><Label>Cell Phone</Label><Input value={formData.parent1_cell || ''} onChange={(e) => setFormData({...formData, parent1_cell: e.target.value})} /></div>
                  <div><Label>Work Phone</Label><Input value={formData.parent1_work_phone || ''} onChange={(e) => setFormData({...formData, parent1_work_phone: e.target.value})} /></div>
                </div>
              </div>
              
              {/* Parent 2 */}
              <div>
                <h3 className="font-semibold mb-3">Parent/Guardian 2</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Full Name</Label><Input value={formData.parent2_name || ''} onChange={(e) => setFormData({...formData, parent2_name: e.target.value})} /></div>
                  <div><Label>Cell Phone</Label><Input value={formData.parent2_cell || ''} onChange={(e) => setFormData({...formData, parent2_cell: e.target.value})} /></div>
                  <div><Label>Work Phone</Label><Input value={formData.parent2_work_phone || ''} onChange={(e) => setFormData({...formData, parent2_work_phone: e.target.value})} /></div>
                </div>
              </div>
              
              <Separator />
              
              {/* Emergency Contact */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Phone className="w-4 h-4" /> Emergency Contact</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Name</Label><Input value={formData.emergency_contact_name || ''} onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})} /></div>
                  <div><Label>Phone</Label><Input value={formData.emergency_contact_phone || ''} onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})} /></div>
                  <div><Label>Relationship</Label><Input value={formData.emergency_contact_relationship || ''} onChange={(e) => setFormData({...formData, emergency_contact_relationship: e.target.value})} /></div>
                </div>
              </div>
              
              {/* Physician */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4" /> Physician/Medical Facility</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Physician Name</Label><Input value={formData.physician_name || ''} onChange={(e) => setFormData({...formData, physician_name: e.target.value})} /></div>
                  <div><Label>Phone</Label><Input value={formData.physician_phone || ''} onChange={(e) => setFormData({...formData, physician_phone: e.target.value})} /></div>
                </div>
                <div className="mt-3"><Label>Address</Label><Input value={formData.physician_address || ''} onChange={(e) => setFormData({...formData, physician_address: e.target.value})} /></div>
                <div className="mt-3"><Label>Preferred Medical Facility</Label><Input value={formData.medical_facility || ''} onChange={(e) => setFormData({...formData, medical_facility: e.target.value})} /></div>
              </div>
              
              <Separator />
              
              {/* Authorized Pickups */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Authorized to Pick Up Child</span>
                  <Button size="sm" variant="outline" onClick={addAuthorizedPickup}><Plus className="w-4 h-4 mr-1" />Add Person</Button>
                </h3>
                <p className="text-xs text-muted-foreground mb-3">Persons other than parents authorized to pick up or accept the child</p>
                {(formData.authorized_pickups || []).map((pickup, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-3 mb-2 p-3 bg-muted/50 rounded">
                    <Input placeholder="Name" value={pickup.name} onChange={(e) => updateAuthorizedPickup(idx, 'name', e.target.value)} />
                    <Input placeholder="Phone" value={pickup.phone} onChange={(e) => updateAuthorizedPickup(idx, 'phone', e.target.value)} />
                    <Input placeholder="Relationship" value={pickup.relationship} onChange={(e) => updateAuthorizedPickup(idx, 'relationship', e.target.value)} />
                  </div>
                ))}
              </div>
              
              <Separator />
              
              {/* Consents */}
              <div>
                <h3 className="font-semibold mb-3">Parental Consents</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 border rounded">
                    <Checkbox checked={formData.emergency_medical_consent || false} onCheckedChange={(c) => setFormData({...formData, emergency_medical_consent: c})} />
                    <div><Label className="font-medium">Emergency Medical Care Consent</Label><p className="text-xs text-muted-foreground">I authorize the center to seek emergency medical care for my child if I cannot be reached.</p></div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded">
                    <Checkbox checked={formData.photo_consent || false} onCheckedChange={(c) => setFormData({...formData, photo_consent: c})} />
                    <div><Label className="font-medium">Photo/Video Consent</Label><p className="text-xs text-muted-foreground">I consent to photos/videos of my child for center activities and displays.</p></div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded">
                    <Checkbox checked={formData.sunscreen_consent || false} onCheckedChange={(c) => setFormData({...formData, sunscreen_consent: c})} />
                    <div><Label className="font-medium">Sunscreen Application Consent</Label><p className="text-xs text-muted-foreground">I consent to staff applying sunscreen to my child during outdoor activities.</p></div>
                  </div>
                </div>
              </div>
              
              {/* Signature */}
              <div className="p-4 bg-muted/50 rounded">
                <h3 className="font-semibold mb-3">Parent/Guardian Signature</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Signature (Type Full Name)</Label><Input value={formData.parent_signature || ''} onChange={(e) => setFormData({...formData, parent_signature: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={formData.signature_date || ''} onChange={(e) => setFormData({...formData, signature_date: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      case 'health_history':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Child's Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={formData.child_dob || ''} onChange={(e) => setFormData({...formData, child_dob: e.target.value})} /></div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Medical Conditions</h3>
                <p className="text-xs text-muted-foreground mb-3">Check all that apply:</p>
                <div className="grid grid-cols-3 gap-3">
                  {['asthma', 'diabetes', 'epilepsy', 'cerebral_palsy', 'food_allergies', 'other'].map((condition) => (
                    <div key={condition} className="flex items-center gap-2">
                      <Checkbox 
                        checked={formData.medical_conditions?.[condition] || false}
                        onCheckedChange={(c) => setFormData({
                          ...formData, 
                          medical_conditions: {...(formData.medical_conditions || {}), [condition]: c}
                        })}
                      />
                      <Label className="capitalize">{condition.replace('_', ' ')}</Label>
                    </div>
                  ))}
                </div>
                <div className="mt-3"><Label>Condition Details</Label><Textarea value={formData.condition_details || ''} onChange={(e) => setFormData({...formData, condition_details: e.target.value})} placeholder="Describe medical conditions in detail..." /></div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Allergies & Emergency Care Plan</h3>
                <div className="space-y-3">
                  <div><Label>Allergies</Label><Input value={formData.allergies || ''} onChange={(e) => setFormData({...formData, allergies: e.target.value})} placeholder="List all allergies" /></div>
                  <div><Label>Triggers that may cause a problem</Label><Textarea value={formData.allergy_triggers || ''} onChange={(e) => setFormData({...formData, allergy_triggers: e.target.value})} /></div>
                  <div><Label>Signs or symptoms to watch for</Label><Textarea value={formData.allergy_symptoms || ''} onChange={(e) => setFormData({...formData, allergy_symptoms: e.target.value})} /></div>
                  <div><Label>Steps staff should follow</Label><Textarea value={formData.allergy_treatment || ''} onChange={(e) => setFormData({...formData, allergy_treatment: e.target.value})} /></div>
                  <div><Label>When to call parent</Label><Textarea value={formData.when_to_call_parent || ''} onChange={(e) => setFormData({...formData, when_to_call_parent: e.target.value})} /></div>
                  <div><Label>When condition requires emergency care</Label><Textarea value={formData.when_emergency_care || ''} onChange={(e) => setFormData({...formData, when_emergency_care: e.target.value})} /></div>
                  <div><Label>Staff trained to help treat symptoms</Label><Input value={formData.trained_staff || ''} onChange={(e) => setFormData({...formData, trained_staff: e.target.value})} /></div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Special Diet / Milk Alternative</h3>
                <div><Label>Special Diet Requirements</Label><Input value={formData.special_diet || ''} onChange={(e) => setFormData({...formData, special_diet: e.target.value})} /></div>
                <div className="mt-3"><Label>Milk Allergy Alternative (medical statement required)</Label><Input value={formData.milk_allergy_alternative || ''} onChange={(e) => setFormData({...formData, milk_allergy_alternative: e.target.value})} /></div>
              </div>
              
              {/* Signature */}
              <div className="p-4 bg-muted/50 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Parent Signature</Label><Input value={formData.parent_signature || ''} onChange={(e) => setFormData({...formData, parent_signature: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={formData.signature_date || ''} onChange={(e) => setFormData({...formData, signature_date: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      case 'immunization':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Child's Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={formData.child_dob || ''} onChange={(e) => setFormData({...formData, child_dob: e.target.value})} /></div>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                <p><strong>Note:</strong> Immunization record must be on file within 30 school days (6 calendar weeks) of first attendance per s. 252.04, Wis. Stats.</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">DTaP (Diphtheria, Tetanus, Pertussis)</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {[0,1,2,3,4].map(i => (
                      <Input key={i} type="date" placeholder={`Dose ${i+1}`} value={formData.dtap_dates?.[i] || ''} 
                        onChange={(e) => { const dates = [...(formData.dtap_dates || ['','','','',''])]; dates[i] = e.target.value; setFormData({...formData, dtap_dates: dates}); }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="font-semibold">Polio (IPV)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {[0,1,2,3].map(i => (
                      <Input key={i} type="date" placeholder={`Dose ${i+1}`} value={formData.polio_dates?.[i] || ''} 
                        onChange={(e) => { const dates = [...(formData.polio_dates || ['','','',''])]; dates[i] = e.target.value; setFormData({...formData, polio_dates: dates}); }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="font-semibold">MMR (Measles, Mumps, Rubella)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {[0,1].map(i => (
                      <Input key={i} type="date" placeholder={`Dose ${i+1}`} value={formData.mmr_dates?.[i] || ''} 
                        onChange={(e) => { const dates = [...(formData.mmr_dates || ['',''])]; dates[i] = e.target.value; setFormData({...formData, mmr_dates: dates}); }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="font-semibold">Hepatitis B</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[0,1,2].map(i => (
                      <Input key={i} type="date" placeholder={`Dose ${i+1}`} value={formData.hepb_dates?.[i] || ''} 
                        onChange={(e) => { const dates = [...(formData.hepb_dates || ['','',''])]; dates[i] = e.target.value; setFormData({...formData, hepb_dates: dates}); }} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="font-semibold">Varicella (Chickenpox)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {[0,1].map(i => (
                      <Input key={i} type="date" placeholder={`Dose ${i+1}`} value={formData.varicella_dates?.[i] || ''} 
                        onChange={(e) => { const dates = [...(formData.varicella_dates || ['',''])]; dates[i] = e.target.value; setFormData({...formData, varicella_dates: dates}); }} />
                    ))}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Exemptions</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2"><Checkbox checked={formData.religious_exemption || false} onCheckedChange={(c) => setFormData({...formData, religious_exemption: c})} /><Label>Religious Conviction Exemption</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={formData.personal_exemption || false} onCheckedChange={(c) => setFormData({...formData, personal_exemption: c})} /><Label>Personal Conviction Exemption</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={formData.medical_exemption || false} onCheckedChange={(c) => setFormData({...formData, medical_exemption: c})} /><Label>Medical Exemption (Physician signature required)</Label></div>
                </div>
              </div>
              
              <div className="p-4 bg-muted/50 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Parent Signature</Label><Input value={formData.parent_signature || ''} onChange={(e) => setFormData({...formData, parent_signature: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={formData.signature_date || ''} onChange={(e) => setFormData({...formData, signature_date: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      case 'field_trip':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              <div><Label>Child's Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
              
              <div className="p-4 border rounded">
                <div className="flex items-start gap-3">
                  <Checkbox checked={formData.blanket_permission || false} onCheckedChange={(c) => setFormData({...formData, blanket_permission: c})} />
                  <div>
                    <Label className="font-semibold">Blanket Field Trip Permission</Label>
                    <p className="text-sm text-muted-foreground">I give permission for my child to participate in ALL field trips involving vehicle transportation. I understand I will be notified of the date, time, and destination prior to each trip.</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border rounded">
                <div className="flex items-start gap-3">
                  <Checkbox checked={formData.transportation_consent || false} onCheckedChange={(c) => setFormData({...formData, transportation_consent: c})} />
                  <div>
                    <Label className="font-semibold">Transportation Consent</Label>
                    <p className="text-sm text-muted-foreground">I consent to my child being transported in center vehicles during field trips.</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border rounded">
                <div className="flex items-start gap-3">
                  <Checkbox checked={formData.emergency_info_carried !== false} onCheckedChange={(c) => setFormData({...formData, emergency_info_carried: c})} />
                  <div>
                    <Label className="font-semibold">Emergency Information</Label>
                    <p className="text-sm text-muted-foreground">I understand emergency contact information will be carried during all field trips.</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-muted/50 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Parent Signature</Label><Input value={formData.parent_signature || ''} onChange={(e) => setFormData({...formData, parent_signature: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={formData.signature_date || ''} onChange={(e) => setFormData({...formData, signature_date: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      case 'alternate_arrival':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              <div><Label>Child's Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Arrival Plan</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Child arrives from</Label><Input value={formData.arrives_from || ''} onChange={(e) => setFormData({...formData, arrives_from: e.target.value})} placeholder="School, Home, etc." /></div>
                  <div><Label>Arrives via</Label>
                    <Select value={formData.arrives_via || ''} onValueChange={(v) => setFormData({...formData, arrives_via: v})}>
                      <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="walks">Walks alone</SelectItem>
                        <SelectItem value="school_bus">School bus</SelectItem>
                        <SelectItem value="taxi">Taxi/Rideshare</SelectItem>
                        <SelectItem value="center_transport">Center transport</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Approximate arrival time</Label><Input type="time" value={formData.arrival_time || ''} onChange={(e) => setFormData({...formData, arrival_time: e.target.value})} /></div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3">Departure Plan</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Child departs to</Label><Input value={formData.departs_to || ''} onChange={(e) => setFormData({...formData, departs_to: e.target.value})} placeholder="School, Home, etc." /></div>
                  <div><Label>Departs via</Label>
                    <Select value={formData.departs_via || ''} onValueChange={(v) => setFormData({...formData, departs_via: v})}>
                      <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="walks">Walks alone</SelectItem>
                        <SelectItem value="school_bus">School bus</SelectItem>
                        <SelectItem value="taxi">Taxi/Rideshare</SelectItem>
                        <SelectItem value="center_transport">Center transport</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Approximate departure time</Label><Input type="time" value={formData.departure_time || ''} onChange={(e) => setFormData({...formData, departure_time: e.target.value})} /></div>
                </div>
              </div>
              
              <div><Label>Transportation Agency (if applicable)</Label><Input value={formData.transportation_agency || ''} onChange={(e) => setFormData({...formData, transportation_agency: e.target.value})} placeholder="Bus company, taxi service, etc." /></div>
              
              <div><Label>Special Instructions</Label><Textarea value={formData.special_instructions || ''} onChange={(e) => setFormData({...formData, special_instructions: e.target.value})} /></div>
              
              <div className="p-4 bg-muted/50 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Parent Signature</Label><Input value={formData.parent_signature || ''} onChange={(e) => setFormData({...formData, parent_signature: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={formData.signature_date || ''} onChange={(e) => setFormData({...formData, signature_date: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      case 'physical_exam':
        return (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                <p><strong>Required Schedule:</strong></p>
                <ul className="list-disc ml-5 mt-1 text-xs">
                  <li>Under 2 years: Initial exam within 6 months prior to 3 months after admission, follow-up every 6 months</li>
                  <li>2-4 years: Initial exam within 1 year prior to 3 months after admission, follow-up every 2 years</li>
                  <li>5+ years: Not required</li>
                </ul>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Child's Name</Label><Input value={formData.child_name || ''} onChange={(e) => setFormData({...formData, child_name: e.target.value})} /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={formData.child_dob || ''} onChange={(e) => setFormData({...formData, child_dob: e.target.value})} /></div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Exam Date</Label><Input type="date" value={formData.exam_date || ''} onChange={(e) => setFormData({...formData, exam_date: e.target.value})} /></div>
                <div><Label>Next Exam Due</Label><Input type="date" value={formData.next_exam_due || ''} onChange={(e) => setFormData({...formData, next_exam_due: e.target.value})} /></div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-semibold mb-3">Examination Results</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Height</Label><Input value={formData.height || ''} onChange={(e) => setFormData({...formData, height: e.target.value})} /></div>
                  <div><Label>Weight</Label><Input value={formData.weight || ''} onChange={(e) => setFormData({...formData, weight: e.target.value})} /></div>
                  <div><Label>Blood Pressure</Label><Input value={formData.blood_pressure || ''} onChange={(e) => setFormData({...formData, blood_pressure: e.target.value})} /></div>
                  <div><Label>Vision</Label><Input value={formData.vision || ''} onChange={(e) => setFormData({...formData, vision: e.target.value})} /></div>
                  <div><Label>Hearing</Label><Input value={formData.hearing || ''} onChange={(e) => setFormData({...formData, hearing: e.target.value})} /></div>
                  <div><Label>General Health</Label>
                    <Select value={formData.general_health || 'good'} onValueChange={(v) => setFormData({...formData, general_health: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="concerns">Concerns Noted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div><Label>Activity Restrictions</Label><Textarea value={formData.restrictions || ''} onChange={(e) => setFormData({...formData, restrictions: e.target.value})} /></div>
              <div><Label>Recommendations</Label><Textarea value={formData.recommendations || ''} onChange={(e) => setFormData({...formData, recommendations: e.target.value})} /></div>
              
              <div className="p-4 bg-muted/50 rounded">
                <h3 className="font-semibold mb-3">Physician Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Physician Name</Label><Input value={formData.physician_name || ''} onChange={(e) => setFormData({...formData, physician_name: e.target.value})} /></div>
                  <div><Label>Physician Signature</Label><Input value={formData.physician_signature || ''} onChange={(e) => setFormData({...formData, physician_signature: e.target.value})} /></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        );

      default:
        return <p>Form not found</p>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">DCF Required Forms</h3>
        <Badge variant="outline" className="text-xs">DCF 251.04(6)</Badge>
      </div>
      
      {/* Forms List */}
      <div className="grid grid-cols-2 gap-3">
        {FORM_TYPES.map((formType) => {
          const status = getFormStatus(formType.id);
          const Icon = formType.icon;
          const isNotRequired = formType.under2Only && !isChildUnder2;
          
          return (
            <Card key={formType.id} className={`cursor-pointer hover:shadow-md transition-shadow ${status.status === 'complete' ? 'border-green-200' : ''} ${isNotRequired ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded ${status.status === 'complete' ? 'bg-green-100' : isNotRequired ? 'bg-gray-100' : 'bg-muted'}`}>
                      <Icon className={`w-5 h-5 ${status.status === 'complete' || status.status === 'uploaded' ? 'text-green-600' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-sm">{formType.label}</h4>
                      <p className="text-[10px] text-muted-foreground">{formType.dcfCode}</p>
                      {isNotRequired ? (
                        <Badge className="mt-1 text-[10px] bg-gray-100 text-gray-600 border-gray-200">
                          <CheckCircle className="w-3 h-3 mr-0.5" />
                          N/A — Child over 2
                        </Badge>
                      ) : (
                        <Badge className={`mt-1 text-[10px] ${status.color}`}>{status.label}</Badge>
                      )}
                      {forms[formType.id]?.uploaded_file && (
                        <p className="text-[9px] text-blue-500 mt-0.5">📎 File attached</p>
                      )}
                    </div>
                  </div>
                  {formType.required && !isNotRequired && <Badge variant="destructive" className="text-[9px]">Required</Badge>}
                  {isNotRequired && <Badge variant="outline" className="text-[9px] bg-gray-50 text-gray-500">Not Required</Badge>}
                </div>
                {!isNotRequired && (
                  <div className="flex gap-1 mt-3">
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => openFormDialog(formType)}>
                      {status.status === 'missing' ? 'Fill Out' : 'Edit'}
                    </Button>
                    {/* Upload Button */}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => handleFileUpload(formType, e)}
                        disabled={uploading[formType.id]}
                      />
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 px-2" 
                        disabled={uploading[formType.id]}
                        asChild
                      >
                        <span>
                          {uploading[formType.id] ? (
                            <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Upload className="w-3 h-3" />
                          )}
                        </span>
                      </Button>
                    </label>
                    {(status.status !== 'missing' || forms[formType.id]?.uploaded_file) && (
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleDownloadPDF(formType)}>
                        <Download className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Provider/Parent Agreement Section */}
      <Card className="border-2 border-purple-200 bg-purple-50/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${agreement.signed ? 'bg-green-100' : 'bg-purple-100'}`}>
                {agreement.signed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Handshake className="w-5 h-5 text-purple-600" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-sm">Provider/Parent Agreement</h4>
                  {agreement.signed_via_sibling_name && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-900 border border-amber-200"
                      title={`This child was covered by ${agreement.signed_via_sibling_name}'s signed agreement — same signature applies here.`}
                      data-testid="agreement-signed-via-chip"
                    >
                      Signed with {agreement.signed_via_sibling_name}'s agreement
                    </span>
                  )}
                  {agreement.provider_signature && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-900 border border-green-200"
                      title={`Countersigned${agreement.provider_signature_date ? ' on ' + agreement.provider_signature_date : ''}`}
                      data-testid="agreement-countersigned-chip"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Countersigned
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {agreement.signed 
                    ? `Signed on ${agreement.signed_date || 'N/A'}${agreement.uploaded_file ? ' • File attached' : ''}${agreement.has_parent_signature_image ? ' • Signature captured' : ''}` 
                    : 'Mark as signed or upload signed agreement'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Checkbox to mark as signed */}
              <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors">
                <Checkbox
                  checked={agreement.signed}
                  onCheckedChange={handleAgreementToggle}
                  disabled={agreementLoading}
                  data-testid="agreement-signed-checkbox"
                />
                <span className="text-sm font-medium">Signed</span>
              </label>
              
              {/* Upload Button */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleAgreementUpload}
                  disabled={uploadingAgreement}
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={uploadingAgreement}
                  asChild
                  data-testid="agreement-upload-btn"
                >
                  <span>
                    {uploadingAgreement ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
              
              {/* Download Button (if file exists) */}
              {agreement.uploaded_file && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadAgreement}
                  data-testid="agreement-download-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              )}

              {/* Countersign button — only enabled once parent signed with an image or is marked signed */}
              <Button
                size="sm"
                onClick={() => setCountersignOpen(true)}
                className={agreement.provider_signature ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="agreement-countersign-btn"
              >
                <PenTool className="w-4 h-4 mr-1" />
                {agreement.provider_signature ? 'Countersigned' : 'Countersign'}
              </Button>
            </div>
          </div>

          {/* Family card — shown when this child has siblings sharing the family agreement */}
          {Array.isArray(agreement.family) && agreement.family.length > 1 && (
            <div className="mt-3 pt-3 border-t border-purple-200" data-testid="agreement-family-card">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-3.5 h-3.5 text-purple-700" />
                <span className="text-xs font-semibold text-purple-900">
                  Family shares this agreement
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {agreement.family.map(member => {
                  const isCurrent = member.child_id === childId;
                  const isPrimary = member.signed && !member.signed_via_sibling_id;
                  return (
                    <div
                      key={member.child_id}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border ${
                        isCurrent
                          ? 'bg-purple-100 border-purple-400 text-purple-900 font-semibold'
                          : member.signed
                          ? 'bg-white border-green-300 text-green-800'
                          : 'bg-white border-gray-200 text-gray-500'
                      }`}
                      data-testid={`agreement-family-member-${member.child_id}`}
                    >
                      {member.signed && <CheckCircle className="w-3 h-3" />}
                      <span>{member.name || 'Child'}</span>
                      {isPrimary && (
                        <span className="text-[9px] uppercase tracking-wide bg-purple-600 text-white px-1 rounded">
                          primary
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Enrollment Link to Parent */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-sm">Parent Self-Service Enrollment</h4>
                <p className="text-xs text-muted-foreground">Send a secure link for parents to fill out forms</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                resetSendLinkDialog();
                fetchExistingLinks();
                setSendLinkDialogOpen(true);
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="send-enrollment-link-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Enrollment Link
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Countersign Dialog */}
      <Dialog open={countersignOpen} onOpenChange={setCountersignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-primary" />
              Countersign Payment Agreement
            </DialogTitle>
            <DialogDescription>
              Add your signature to the parent's signed DCF-F-CFS5224 for {childName}.
              This will appear on the PDF and, if the parent's agreement covers siblings,
              your countersignature is applied to their records too.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Your name (appears on the PDF)</Label>
              <Input
                placeholder={user?.name || 'e.g., Jane Director'}
                value={countersignName}
                onChange={(e) => setCountersignName(e.target.value)}
                data-testid="countersign-name-input"
              />
            </div>
            <div>
              <Label className="block mb-2">Your signature *</Label>
              <SignaturePad
                value={countersignSig}
                typedName={countersignTyped}
                onTypedNameChange={setCountersignTyped}
                onChange={setCountersignSig}
                testId="countersign-signature"
              />
            </div>
            <div>
              <Label>Date signed</Label>
              <Input
                type="date"
                value={countersignDate}
                onChange={(e) => setCountersignDate(e.target.value)}
                data-testid="countersign-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCountersignOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCountersignSubmit}
              disabled={countersigning || !countersignSig}
              data-testid="countersign-submit-btn"
            >
              {countersigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenTool className="w-4 h-4 mr-2" />}
              Save countersignature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Enrollment Link Dialog */}
      <Dialog open={sendLinkDialogOpen} onOpenChange={setSendLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Send Enrollment Link
            </DialogTitle>
            <DialogDescription>
              Send a secure link to parents to fill out DCF forms for {childName}
            </DialogDescription>
          </DialogHeader>

          {!sentLink ? (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Parent Name</Label>
                <Input
                  placeholder="Parent's name"
                  value={magicLinkData.parent_name}
                  onChange={(e) => setMagicLinkData({...magicLinkData, parent_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Parent Email *</Label>
                <Input
                  type="email"
                  placeholder="parent@example.com"
                  value={magicLinkData.parent_email}
                  onChange={(e) => setMagicLinkData({...magicLinkData, parent_email: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Forms to Complete</Label>
                <div className="grid grid-cols-1 gap-2 border rounded-lg p-3">
                  {[
                    { id: 'enrollment', label: 'Child Care Enrollment (DCF-F-CFS0062)' },
                    { id: 'health_history', label: 'Health History & Emergency Care (DCF-F-CFS2345-E)' },
                    { id: 'health_report', label: 'Child Health Report (DCF-F-CFS0060-E)' },
                    { id: 'intake_under2', label: 'Intake for Child Under 2 (DCF-F-CFS0061)' },
                    { id: 'payment_agreement', label: 'Provider/Parent Payment Agreement (DCF-F-CFS5224)' }
                  ].map(form => (
                    <label key={form.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <Checkbox
                        checked={magicLinkData.forms_to_complete.includes(form.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setMagicLinkData({
                              ...magicLinkData,
                              forms_to_complete: [...magicLinkData.forms_to_complete, form.id]
                            });
                          } else {
                            setMagicLinkData({
                              ...magicLinkData,
                              forms_to_complete: magicLinkData.forms_to_complete.filter(f => f !== form.id)
                            });
                          }
                        }}
                      />
                      <span className="text-sm">{form.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Multi-child: sibling picker */}
              {siblings.length > 0 && (
                <div className="space-y-2" data-testid="magic-link-siblings">
                  <Label>Also cover siblings</Label>
                  <p className="text-xs text-muted-foreground">
                    One link — the parent will see each child's forms in the same queue.
                  </p>
                  <div className="grid grid-cols-1 gap-2 border rounded-lg p-3 bg-amber-50/40">
                    {siblings.map(sib => {
                      const checked = selectedSiblingIds.includes(sib.id);
                      return (
                        <label
                          key={sib.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded"
                          data-testid={`magic-link-sibling-${sib.id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setSelectedSiblingIds(prev =>
                                v ? [...prev, sib.id] : prev.filter(x => x !== sib.id)
                              );
                            }}
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium">{sib.name}</span>
                            {sib.date_of_birth && (
                              <span className="text-xs text-gray-500 ml-2">DOB {sib.date_of_birth}</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Existing Links Section */}
              {existingLinks.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-xs text-gray-500">Previously Sent Links</Label>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {existingLinks.filter(l => l.status !== 'completed').slice(0, 5).map(link => {
                      const missing = link.per_child_missing || [];
                      const totalMissing = missing.reduce((n, m) => n + (m.missing_forms?.length || 0), 0);
                      return (
                        <div key={link.id} className="p-2 bg-gray-50 rounded text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{link.parent_email}</p>
                              <p className="text-gray-500">
                                {link.status === 'partial' ? '⏳ Partial' : link.status === 'pending' ? '📩 Pending' : link.status}
                                {link.is_multi_child && ` • ${(link.child_ids || []).length} children`}
                                {totalMissing > 0 && ` • ${totalMissing} form${totalMissing !== 1 ? 's' : ''} pending`}
                                {link.reminder_count > 0 && ` • ${link.reminder_count} reminder(s)`}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-amber-600 hover:text-amber-700 shrink-0"
                              onClick={() => handleSendReminder(link.id)}
                              disabled={sendingReminder === link.id}
                              data-testid={`resend-link-${link.id}`}
                            >
                              {sendingReminder === link.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          </div>
                          {/* Per-child missing forms breakdown */}
                          {missing.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1" data-testid={`link-missing-${link.id}`}>
                              {missing.map(cm => (
                                <div key={cm.child_id} className="flex items-start gap-2">
                                  <span className="font-medium text-gray-700 min-w-[80px] truncate">
                                    {cm.child_name}:
                                  </span>
                                  <div className="flex flex-wrap gap-1">
                                    {cm.missing_forms.map(f => (
                                      <span
                                        key={f}
                                        className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium"
                                      >
                                        {f.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSendLinkDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleSendMagicLink}
                  disabled={sendingLink}
                >
                  {sendingLink ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Link
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-700">Link Sent Successfully!</p>
                <p className="text-sm text-green-600">
                  An email has been sent to {magicLinkData.parent_email}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Direct Link (valid for 7 days)</Label>
                <div className="flex gap-2">
                  <Input value={sentLink.link} readOnly className="text-xs" />
                  <Button variant="outline" size="sm" onClick={copyLinkToClipboard}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                <p><strong>Expires:</strong> {new Date(sentLink.expires_at).toLocaleDateString()}</p>
                <p><strong>Forms:</strong> {magicLinkData.forms_to_complete.length} form(s) selected</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    resetSendLinkDialog();
                  }}
                >
                  Send Another
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => setSendLinkDialogOpen(false)}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeForm && <activeForm.icon className="w-5 h-5" />}
              {activeForm?.label}
            </DialogTitle>
            <DialogDescription>{activeForm?.dcfCode} - Wisconsin Child Care Licensing</DialogDescription>
          </DialogHeader>
          
          {renderFormContent()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveForm}>Save Form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DCF Enrollment Form - Official DCF-F-CFS0062 */}
      <DCFEnrollmentForm
        open={dcfEnrollmentOpen}
        onClose={() => {
          setDcfEnrollmentOpen(false);
          fetchForms(); // Refresh to get updated status
          if (onUpdate) onUpdate();
        }}
        childId={childId}
        childName={childName}
        token={token}
      />

      {/* DCF Health Report Form - Official DCF-F-CFS0060-E */}
      <DCFHealthReportForm
        open={dcfHealthReportOpen}
        onClose={() => {
          setDcfHealthReportOpen(false);
          fetchForms(); // Refresh to get updated status
          if (onUpdate) onUpdate();
        }}
        childId={childId}
        childName={childName}
        token={token}
      />

      {/* DCF Intake Under 2 Form - Official DCF-F-CFS0061 */}
      <DCFIntakeUnder2Form
        open={dcfIntakeUnder2Open}
        onClose={() => {
          setDcfIntakeUnder2Open(false);
          fetchForms(); // Refresh to get updated status
          if (onUpdate) onUpdate();
        }}
        childId={childId}
        childName={childName}
        token={token}
      />

      {/* DCF Health History and Emergency Care Plan - Official DCF-F-CFS2345-E */}
      <DCFHealthHistoryForm
        open={dcfHealthHistoryOpen}
        onClose={() => {
          setDcfHealthHistoryOpen(false);
          fetchForms(); // Refresh to get updated status
          if (onUpdate) onUpdate();
        }}
        childId={childId}
        childName={childName}
        token={token}
      />
    </div>
  );
};

export default ChildRecordsForms;
